import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, BadgeCheck, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { useContent } from "../contexts/ContentContext";

export default function Hero({ onEnquire }) {
  const { content } = useContent();
  const { heroSlides, trustBadges } = content;
  const [idx, setIdx] = useState(0);
  const total = heroSlides.length;

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6000);
    return () => clearInterval(t);
  }, [total]);

  const slide = heroSlides[idx];

  return (
    <section id="home" className="relative pt-20 bg-[#0a3d38] overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-orange-500/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pt-12 lg:pt-20 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* LEFT */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/15 border border-orange-400/30 text-orange-300 text-xs font-semibold tracking-wide mb-6">
              <BadgeCheck className="w-4 h-4" />
              {slide.eyebrow} • {slide.eyebrowNote}
            </div>
            <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
              <span className="text-orange-400">{slide.title}</span>
              <br />
              <span className="text-white">{slide.titleAccent}</span>
            </h1>
            <p className="mt-6 text-slate-200 text-lg max-w-xl leading-relaxed">
              {slide.description}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button
                onClick={onEnquire}
                className="bg-orange-500 hover:bg-orange-600 text-white px-7 h-12 rounded-md font-semibold shadow-lg hover:shadow-orange-500/30 transition-shadow"
              >
                {slide.cta} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <a
                href="#calculator"
                className="px-6 h-12 inline-flex items-center text-white/90 hover:text-white border border-white/30 rounded-md font-medium hover:border-white transition-colors"
              >
                Calculate Savings
              </a>
            </div>

            {/* Trust badges */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {trustBadges.map((b) => (
                <div key={b.label} className="px-3 py-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="text-orange-400 font-extrabold text-xl">{b.value}</div>
                  <div className="text-slate-300 text-xs mt-0.5">{b.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT - image */}
          <div className="relative">
            <div className="aspect-[4/5] lg:aspect-[5/6] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 relative">
              {heroSlides.map((s, i) => (
                <img
                  key={s.image || s.title || `slide-${i}`}
                  src={s.image}
                  alt={s.title}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    i === idx ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0a3d38]/60 via-transparent to-transparent" />
              {/* Floating badge */}
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-white/95 backdrop-blur shadow-xl">
                <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">PM</div>
                <div>
                  <div className="text-[#0a4f47] font-bold leading-tight">PM Surya Ghar Muft Bijli Yojana</div>
                  <div className="text-xs text-slate-600">Get subsidy up to ₹78,000 • We file all paperwork</div>
                </div>
              </div>
            </div>

            {/* Arrows */}
            <button
              onClick={() => setIdx((i) => (i - 1 + total) % total)}
              className="absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-[#0a4f47] shadow-lg hover:bg-orange-500 hover:text-white transition-colors flex items-center justify-center"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIdx((i) => (i + 1) % total)}
              className="absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-[#0a4f47] shadow-lg hover:bg-orange-500 hover:text-white transition-colors flex items-center justify-center"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {heroSlides.map((s, i) => (
                <span
                  key={`dot-${s.image || s.title || i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-orange-500" : "w-2 bg-white/50"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
